import { ReactNode } from "react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import AppSidebar from "./AppSidebar";
import Topbar from "./Topbar";
import { useUserRoles } from "@/hooks/useUserRoles";
import { AppShellContext } from "./AppShellContext";
import type { AppRole } from "@/lib/nav";

interface AppShellProps {
  children: ReactNode;
  /** Override the role detection (rare, e.g. admin viewing student-only views). */
  roleOverride?: AppRole;
}

/**
 * Shared authed-app shell:
 *  - Collapsible left sidebar driven by the user's primary role.
 *  - Sticky topbar with breadcrumb + user menu.
 *  - Page content slot.
 *
 * Marketing/public routes (/, /auth, /parent/auth) keep the legacy
 * <Header /> and do NOT use this shell.
 */
export default function AppShell({ children, roleOverride }: AppShellProps) {
  const { primaryRole, loading } = useUserRoles();
  const role = roleOverride ?? primaryRole;

  // Render shell immediately even while roles load; sidebar will populate
  // once the primary role resolves. This keeps perceived nav stable.
  return (
    <AppShellContext.Provider value={{ inShell: true }}>
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar role={role} />
          <SidebarInset className="flex-1 flex flex-col min-w-0">
            <Topbar role={role} />
            <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </AppShellContext.Provider>
  );
}
